import * as STD           from '@emersya/mpa-std';

class Manager {
	static isInitialized = false;
	static isInitializing = false;
	//Too cumberstone to check all times if root is not null
	//Furthermore the check should be done only one times and at the launch so do like that
	static root = <TDOMComponent<any, any, any>><any>null;
	//Too cumberstone to check all times if root is not null
	//Furthermore the check should be done only one times and at the launch so do like that
	static manifest = <TManifest.ManagerManifest><any>null;
	static storeTypes: Map<string, TComponent.TAnyConcrete> = new Map;
	static storeInstances: Set<TComponent.TAny> = new Set;
	static storeInstancesByType: Map<TComponent.TAnyConcrete, TComponent.TAny[]> = new Map;
	static componentStaticProxySymbol = Symbol('component-static-proxy');
	private static declaredComponentBeforeInitialization = <Manager.IDeclareDescriptor<any>[]>[];
	static componentStaticProxy = {
		//declare construct as a member of Component to use protected / privates members
		construct: (async function construct(
			this: TComponent<any, any>,
			target: TComponent.TAnyConcrete,
			args: unknown[]
		) {
			let component: TComponent<any, any> | null = null;
			try {
				component = new target(...args);
				await component.component_finalize();
				return component;
			} catch (error: any) {
				if (component) {
					throw STD.errors.bindAsBound(
						`Cannot instantiate component<${component.component_getPath()}>!`,
						{
							target,
							args,
							component
						},
						error
					);
				}
				const desc = <TComponent.TDescriptor>args[0];
				const path = (desc.parent ? (desc.parent.component_getPath(true) + '.') : '') + desc.id;
				throw STD.errors.bindAsBound(
					'Cannot instantiate a component!',
					{
						target,
						args,
						path
					},
					error
				);
			}
		}).bind(null as any as TComponent<any, any>),  //eslint-disable-line no-extra-bind
		get(
			target: TComponent.TAnyConcrete,
			propertyName: string
		): unknown {
			const classes = target.inheritanceChain;
			const v0 = (<any>target)[propertyName];
			if (v0 !== undefined) {
				return v0;
			}
			for (const t of classes.slice(1)) {
				const v1 = (<any>t)[propertyName];
				if (v1) {
					return v1;
				}
			}
			return;
		}
	};

	static declareAbstract<T extends TComponent.TAbstract>(
		desc: Manager.IDeclareAbstractDescriptor<T>
	) {
		try {
			if (!this.isInitialized && !this.isInitializing) {
				this.declaredComponentBeforeInitialization.push(desc);
				return;
			}
			desc.type.type = desc.id;
			const Class = desc.type;
			Class.declarationDescriptor = desc;
			this.storeTypes.set(desc.id, <TComponent.TAnyConcrete><any>Class);
			this.storeInstancesByType.set(<TComponent.TAnyConcrete><any>Class, []);
			return Class as typeof Class;
		} catch (error: any) {
			throw STD.errors.bindAsBound('Cannot declare a component!', {
				descriptor: desc
			}, error);
		}
	};

	static declare<T extends TComponent.TAnyConcrete>(
		desc: Manager.IDeclareDescriptor<T>
	) {
		try {
			if (!this.isInitialized && !this.isInitializing) {
				this.declaredComponentBeforeInitialization.push(desc);
				return;
			}
			desc.type.type = desc.id;
			const Class = new Proxy(desc.type, this.componentStaticProxy);
			(<any>desc.type)[this.componentStaticProxySymbol] = Class;
			Class.declarationDescriptor = desc;
			this.storeTypes.set(desc.id, Class);
			this.storeInstancesByType.set(Class, []);
			return Class as typeof Class;
		} catch (error: any) {
			throw STD.errors.bindAsBound('Cannot declare a component!', {
				descriptor: desc
			}, error);
		}
	};

	static initialize(_triggerRender?: () => void) {
		this.isInitializing = true;
		for (const toDeclare of this.declaredComponentBeforeInitialization) {
			this.declare(toDeclare);
		}
		this.isInitialized = true;
		this.isInitializing = false;
	};

	static async instantiate(desc: Manager.TInstantiateDescriptor) {
		const C = this.storeTypes.get(desc.type);
		if (!C) {
			throw STD.errors.user(
				`Manager.instantiate: Unknown type<${desc.type}>`,
				{
					descriptor: desc
				}
			);
		}
		if (!C.hasOwnProperty('isFullyDeclared') || !C.isFullyDeclared) {
			C.finalizeDeclaration();
		}
		const realDescriptor: TComponent.TDescriptor = {
			type: desc.type,
			id: desc.id,
			parent: desc.parent || null,
			isRoot: desc.isRoot,
			tree: desc.tree,
			slot: desc.slot,
			...(desc.isSlot ? {
				isSlot: true,
				declaredSlot: desc.declaredSlot
			} : {
				isSlot: false
			})
		};
		const c = await (<Promise<TComponent<any, any>>><any>new C(realDescriptor, desc.configuration));
		return c;
	};

	static setManifest(newManifest: TManifest.ManagerManifest) {
		if (this.manifest) {
			throw STD.errors.user('Manifest already defined!', {
				currentManifest: this.manifest,
				newManifest
			});
		}
		this.manifest = newManifest;
	};

	static setRoot(newRoot: TDOMComponent<any, any, any>) {
		if (this.root) {
			throw STD.errors.user('Root already defined!', {
				currentRoot: this.root,
				newRoot
			});
		}
		this.root = newRoot;
	};

	static generatePropertyEvaluateVariables(
		origin: TComponent<any, any>,
		context: TCondition.TCondition | STD.events.Event<any, TComponent<any, any>>,
		target: TComponent<any, any>
	) {
		return {
			ORIGIN: origin,
			TARGET: target,
			EVENT: (context instanceof STD.events.Event) ? context : null,
			CONDITION: !(context instanceof STD.events.Event) ? context : null
		};
	};

	static getClassProxyOrThrow(className: string) {
		const ComponentProxy = this.storeTypes.get(className);
		if (!ComponentProxy) {
			throw STD.errors.user(`Component type<${className}> does not exists!`, {
				className,
				existentClasses: Array.from(this.storeTypes.keys())
			});
		}
		return ComponentProxy;
	};

	// This algorithm is incomprehensible!!!
	// @TODO : write a comprehensible algorithm!!!
	static async mapFormalEventActionParameterToEffective(
		event: STD.events.Event<any, TComponent<any, any>>,
		parameterValue: any,
		host: TComponent<any, any>
	): Promise<unknown> {
		//return as it if it's not an object or is nulled
		if (!parameterValue || (typeof parameterValue !== 'object')) {
			return parameterValue;
		}
		//Return subvalues remapped
		if (parameterValue instanceof Array) {
			return Promise.all(parameterValue.map(
				async x => await this.mapFormalEventActionParameterToEffective(event, x, host)
			));
		}
		//return one value
		if (parameterValue.$ !== undefined) {
			//return the STD.events.Event directly
			if (parameterValue.$ === null) {
				return event;
			}
			//return the value hosted on the STD.events.Event as formal value representing the path
			return await STD.object.PropertyPath.evaluate(parameterValue.$, event);
		}
		//return one value
		if (parameterValue['@'] === null) {
			return host;
		}
		//return a real object, if it contains property where their id match the pattern "$.MyId"
		//MyId would be used as property name
		return Object.entries(parameterValue).reduce(async (out, [k, v]) => {
			if (k.startsWith('$.')) {
				k = k.slice(2);
				if (typeof v !== 'string') {
					// @TODO : Write a good error when the algorithm will be comprehensible.
					throw STD.errors.user('', {});
				}
				v = await STD.object.PropertyPath.evaluate(v, event);
			} else {
				v = await this.mapFormalEventActionParameterToEffective(event, v, host);
			}
			out[k] = v;
			return out;
		}, <Record<any, any>>{});
	};

	static getComponentsPathAndInstanceCouples(): [TPath.TPathRaw, TComponent<any, any>][] {
		return Array.from(this.storeInstances).map(x => [x.component_getPath(), x]);
	};

	private constructor() {
		STD.errors.user('Manager class is static and so is not instantiable!', {});
	};
};
namespace Manager {
	export type IInstantiateNotSlotDescriptor = Omit<
		TComponent.IDescriptorNotSlot, 'parent'
	> & {
		configuration: TComponent.IConfiguration;
		parent?: STD.object.PropertyType<TComponent.TDescriptor, 'parent'>;
	};
	export type IInstantiateSlotDescriptor = Omit<
		TComponent.IDescriptorSlot, 'parent'
	> & {
		configuration: TComponent.IConfiguration;
		parent?: STD.object.PropertyType<TComponent.TDescriptor, 'parent'>;
	};
	export type TInstantiateDescriptor = IInstantiateNotSlotDescriptor | IInstantiateSlotDescriptor;

	export interface IDeclareAbstractDescriptor<T extends TComponent.TAbstract> {
		id: string;
		type: T;
		manifest?: string;
	};

	export interface IDeclareDescriptor<T extends TComponent.TAnyConcrete> {
		id: string;
		type: T;
		manifest?: string;
	};

}

export default Manager;
/** Disable eslint for these imports because it would be TOO MUCH verbose to use only one import for each! **/
import type TDOMComponent from './DOMComponent';
import type TComponent    from './Component';
import type TManifest     from './Manifest';
import type TPath         from './Path';
import type TCondition    from './Condition';

// export interface IEventPath {
// 	id : string;
// 	path : ComponentPath.TComponentPathRaw;
// };
// export type TAction = string;
// export type TSlotId = string | null;
